// An Assessment on the learner's dashboard: a program card in its stage's
// column, named with the date its run started (each run is its own card), and
// the panel that opens from it with the manager's message and Continue. The
// Assessments a learner is sent, and how far they are through each, come from
// GET /v3/member/assessments.

import React from 'react';
import { LEVEL_COLOR, LEVEL_NAME } from '../../Program/components/learning-lifecycle/CapabilityGapsList';
import type { MemberProgramCardData } from '../../home/member-learning-lifecycle/types';
import type { ProgramTypeKey } from '../../Program/components/learning-lifecycle/types';
import type { AssessmentCardView } from '../../../../../api/capability';
import { DONE } from '../designSystem';
import { formatDate } from '../../../../../utils/formatDate';
import { ProgramPanel, type PanelLine } from './ProgramPanel';

export const isAssessmentId = (id: string) => id.startsWith('assessment:');

const publishedOn = (a: { createdAt: string }) => formatDate(a.createdAt);
/** Each run is its own card, told apart by the date it started. */
export const assessmentTitle = (a: { name: string; createdAt: string }) => `${a.name} | ${publishedOn(a)}`;

const doneCount = (a: AssessmentCardView) =>
  a.items.filter((i) => a.progress.items[i.capabilityId]?.roleplayDone && a.progress.items[i.capabilityId]?.quizDone)
    .length;

/** Each Assessment as a card for the learner's board. */
export const assessmentCards = (assessments: AssessmentCardView[], pillarIds: string[]): MemberProgramCardData[] =>
  assessments.map((a) => {
    const done = doneCount(a);
    return {
      id: a.id,
      // Its own stage when that column exists; otherwise the first column.
      learningPillarId: a.stageId && pillarIds.includes(a.stageId) ? a.stageId : pillarIds[0] ?? '',
      name: assessmentTitle(a),
      programType: (a.category as ProgramTypeKey | null) ?? 'role-excellence',
      model: 'masterclass',
      roles: [],
      status: a.status,
      dueBy: null,
      mcqScore: null,
      // About four minutes per capability: one Roleplay and one Quiz Question.
      timeRemainingMinutes: (a.items.length - done) * 4,
      goalProgress: a.items.length ? Math.round((done / a.items.length) * 100) : 0,
      owner: { id: '', name: a.ownerName ?? '' },
      unresolvedLessons: 0,
      resumeLesson: null
    };
  });

/**
 * What opens from an Assessment's card: the manager's message, what it covers,
 * and Start / Continue (or, once it is done, the level reached on each capability).
 */
export const AssessmentPanel: React.FC<{
  assessment: AssessmentCardView | null;
  onClose: () => void;
  onContinue: (assessmentId: string) => void;
}> = ({ assessment, onClose, onContinue }) => {
  if (!assessment) return null;
  const done = doneCount(assessment);
  const complete = assessment.status === 'completed';
  const lines: PanelLine[] = assessment.items.map((item) => {
    const result = assessment.progress.items[item.capabilityId];
    const finished = Boolean(result?.roleplayDone && result?.quizDone);
    // A level shows only once the whole Assessment is done (the backend sends it only then).
    if (complete && result?.level) {
      return { label: item.capabilityName, value: LEVEL_NAME[result.level], color: LEVEL_COLOR[result.level] };
    }
    return { label: item.capabilityName, value: finished ? 'Done' : 'To do', color: finished ? DONE : undefined };
  });
  const started = assessment.status !== 'not-started';
  return (
    <ProgramPanel
      open
      kind="assessment"
      title={assessmentTitle(assessment)}
      from={assessment.ownerName ? `From ${assessment.ownerName}` : 'From Your Manager'}
      message={assessment.message}
      linesTitle={
        complete ? 'Your Scores' : `${assessment.items.length} Capabilities, One Roleplay And One Quiz Question Each`
      }
      lines={lines}
      note={
        complete ? undefined : 'You answer each Roleplay once. You can pause at any time and your answers are saved.'
      }
      primary={
        complete
          ? undefined
          : { label: started || done > 0 ? 'Open' : 'Start', onClick: () => onContinue(assessment.id) }
      }
      onClose={onClose}
    />
  );
};
